import React, { useState } from 'react';
import { 
  findBenchmark,
  KinematicParamKey 
} from '../config/kinematicsBenchmarks';
import { computeScoring, ScoringResult } from '../utils/kinematicsScoring';
import { getV2Response, getScoredTier1Count } from '../utils/kinematicsV2Adapter';
import VideoKinematicsKPI from './VideoKinematicsKPI';
import DebugVideoCard from './DebugVideoCard';
import MetricDetailsTable from './MetricDetailsTable';
import SingleCameraNotice from './SingleCameraNotice';
import './VideoKinematicsResultsPanel.css';

/**
 * Video Kinematics Results Panel
 * 
 * Displays kinematic metrics analysis results from video pipeline
 */

export interface VideoKinematicsData {
  movement_name: string;
  movementType?: string; // camelCase alias
  ageBand: string;
  level: "Development" | "Standard" | "Elite";
  kickingFoot: string;
  timestamp: string;
  video: {
    fps: number;
    frameCount: number;
    width: number;
    height: number;
    frameStep?: number;
    debugUrl?: string; // Optional debug video URL
  };
  keyFrames: {
    backswing_frame: number;
    contact_frame: number;
    follow_through_frame: number;
  };
  metrics: Record<KinematicParamKey, number>;
  metricsRangeStatus?: Record<KinematicParamKey, "BELOW_RANGE" | "IN_RANGE" | "ABOVE_RANGE" | null>; // From API
  overallScore?: number; // camelCase
  overall_score?: number; // snake_case (for backwards compatibility)
  debugVideoUrl?: string | null; // Optional debug video URL (top-level)
  debug_video_url?: string | null; // snake_case variant
  debugVideo?: string | null; // Alternative field name
  v2?: {
    version: "2.0";
    overall_score: number | null;
    cohort: {
      cohort_id: string;
      shot_type: string;
      config_source: "default" | "cohort";
      config_version: string;
    };
    tier1_metrics: Array<{
      key: string;
      name: string;
      value: number;
      unit: string;
      confidence_level: "HIGH" | "MEDIUM" | "LOW";
      tier: 1;
      target_range: { min: number; max: number };
      is_scored: boolean;
      score: number | null;
      status: "IN_RANGE" | "BELOW" | "ABOVE" | "NO_SCORE";
      notes: string[];
    }>;
    tier2_metrics: Array<{
      key: string;
      name: string;
      value: number;
      unit: string;
      confidence_level: "HIGH" | "MEDIUM" | "LOW";
      tier: 2;
      target_range: { min: number; max: number } | null;
      is_scored: false;
      score: null;
      status: "NO_SCORE";
      notes: string[];
    }>;
    notes: string[];
    legacy?: {
      raw_metrics: Record<string, number>;
    };
  };
}

interface VideoKinematicsResultsPanelProps {
  data: VideoKinematicsData;
  analysisId?: number;
  playerId?: number;
  isSaved?: boolean;
  onSave?: () => void;
  onDelete?: () => void;
  onBack?: () => void;
}

const VideoKinematicsResultsPanel: React.FC<VideoKinematicsResultsPanelProps> = ({ 
  data, 
  analysisId,
  playerId,
  isSaved = false,
  onSave,
  onDelete,
  onBack
}) => {
  const [showV2, setShowV2] = useState(true); // Default to showing V2 if available
  
  // Check for V2 response
  const v2Response = getV2Response(data);
  const useV2 = v2Response !== null && showV2;
  
  // Find the matching benchmark set (for legacy fallback)
  const benchmark = findBenchmark(data.movement_name, data.ageBand, data.level);
  
  if (!benchmark && !useV2) {
    return (
      <div className="kinematics-panel-error">
        <p>Error: No benchmark found for {data.movement_name}, {data.ageBand}, {data.level}</p>
      </div>
    );
  }

  // Compute scoring - use API-provided rangeStatus if available, otherwise compute client-side
  // Only compute legacy scoring if not using V2
  let scoringResult: ScoringResult | null = null;
  if (!useV2 && benchmark) {
    scoringResult = computeScoring(
      data.metrics, 
      benchmark.params,
      undefined, // weights (use default)
      data.metricsRangeStatus // Pass API-provided rangeStatus if available
    );
  }

  // Extract debug video URL from multiple possible locations
  const debugVideoUrl =
    data.debugVideoUrl ??
    data.video?.debugUrl ??
    data.debug_video_url ??
    data.debugVideo ??
    null;
  
  // Get overall score (V2 or legacy)
  const overallScore = useV2 ? v2Response!.overall_score : (scoringResult?.overallScore ?? null);

  return (
    <div className="video-kinematics-panel">
      <div className="panel-header">
        <h2>Video Kinematics Results</h2>
        <div className="panel-meta">
          <span className="meta-item">
            <strong>Movement:</strong> {data.movement_name}
          </span>
          <span className="meta-item">
            <strong>Age Band:</strong> {data.ageBand}
          </span>
          <span className="meta-item">
            <strong>Level:</strong> {data.level}
          </span>
          <span className="meta-item">
            <strong>Kicking Foot:</strong> {data.kickingFoot}
          </span>
        </div>
      </div>

      {/* V2/Legacy Toggle */}
      {v2Response && (
        <div className="v2-toggle" style={{ marginBottom: '1rem', padding: '0.75rem', background: '#e3f2fd', borderRadius: '4px', border: '1px solid #2196f3' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer', color: '#1976d2' }}>
            <input
              type="checkbox"
              checked={showV2}
              onChange={(e) => setShowV2(e.target.checked)}
            />
            <span style={{ fontWeight: '500', color: '#1976d2' }}>Use V2 Analysis (Tiered, Confidence-Gated)</span>
            {v2Response.cohort && (
              <span style={{ fontSize: '0.85em', color: '#1565c0', fontWeight: '500' }}>
                (Cohort: {v2Response.cohort.cohort_id})
              </span>
            )}
          </label>
        </div>
      )}

      {/* KPI and Debug Video - Side by Side */}
      <div className="kpi-video-row">
        <div className="kpi-card-wrapper">
          {useV2 && v2Response ? (
            <div className="v2-kpi" style={{ 
              background: '#ffeb3b', 
              borderRadius: '8px', 
              padding: '0.75rem',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
              color: '#333',
              maxHeight: '50%',
              aspectRatio: '1 / 1',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center'
            }}>
              <div className="kpi-score" style={{ textAlign: 'center', width: '100%' }}>
                <div className="kpi-value" style={{ fontSize: '7.5rem', fontWeight: 'bold', color: overallScore !== null && overallScore >= 80 ? '#4caf50' : overallScore !== null && overallScore >= 60 ? '#ff9800' : '#f44336', textAlign: 'center' }}>
                  {overallScore !== null ? overallScore.toFixed(1) : 'N/A'}
                </div>
                <div className="kpi-label" style={{ fontSize: '4.25rem', marginTop: '0.25rem', color: '#333', fontWeight: '500', textAlign: 'center' }}>Overall Score</div>
                {overallScore === null && (
                  <div className="kpi-note" style={{ fontSize: '3.5em', color: '#666', marginTop: '0.25rem', fontWeight: '500', textAlign: 'center' }}>
                    {getScoredTier1Count(v2Response)} scored Tier 1 metrics (need 3+)
                  </div>
                )}
              </div>
            </div>
          ) : scoringResult ? (
            <VideoKinematicsKPI scoringResult={scoringResult} />
          ) : (
            <div style={{ padding: '1rem', textAlign: 'center', color: '#999' }}>
              No scoring data available
            </div>
          )}
        </div>
        <div className="debug-video-card-wrapper">
          <DebugVideoCard
            debugVideoUrl={debugVideoUrl}
            isSaved={isSaved}
            playerId={playerId}
            analysisId={analysisId}
          />
        </div>
      </div>

      {/* V2 Metrics Display */}
      {useV2 && v2Response && (
        <div className="v2-metrics-section" style={{ marginTop: '0' }}>
          {/* Tier 1 Metrics */}
          <div className="tier1-metrics" style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginTop: '0', marginBottom: '1rem', color: '#64b5f6', fontWeight: 'bold', fontSize: '1.3em' }}>
              Tier 1 Metrics (Score-Eligible)
            </h3>
            <div className="metrics-grid">
              {v2Response.tier1_metrics.map((metric) => {
                // Determine background color based on status (like previous version)
                let cardBackground = '#f5f5f5'; // Default gray for NO_SCORE
                let textColor = '#333'; // Dark text for visibility
                let borderColor = '#ddd';
                
                if (metric.is_scored && metric.status === 'IN_RANGE') {
                  cardBackground = '#e8f5e9'; // Light green
                  borderColor = '#4caf50';
                } else if (metric.is_scored && metric.status === 'BELOW') {
                  cardBackground = '#fff3e0'; // Light orange/yellow
                  borderColor = '#ff9800';
                } else if (metric.is_scored && metric.status === 'ABOVE') {
                  cardBackground = '#fff3e0'; // Light orange/yellow
                  borderColor = '#ff9800';
                } else {
                  // NO_SCORE - light gray
                  cardBackground = '#f5f5f5';
                  borderColor = '#bbb';
                }
                
                return (
                  <div
                    key={metric.key}
                    className="metric-card"
                    style={{
                      border: `2px solid ${borderColor}`,
                      borderRadius: '8px',
                      padding: '1rem',
                      marginBottom: '1rem',
                      background: cardBackground,
                      color: textColor
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                      <div>
                        <strong style={{ color: textColor, fontSize: '1em' }}>{metric.name}</strong>
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.85em', color: '#555', fontWeight: '500' }}>
                          {metric.value.toFixed(1)}{metric.unit}
                        </span>
                      </div>
                      <span
                        className={`confidence-badge confidence-${metric.confidence_level.toLowerCase()}`}
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          fontSize: '0.75em',
                          fontWeight: 'bold',
                          background: metric.confidence_level === 'HIGH' ? '#4caf50' : 
                                     metric.confidence_level === 'MEDIUM' ? '#ff9800' : '#f44336',
                          color: '#fff'
                        }}
                      >
                        {metric.confidence_level}
                      </span>
                    </div>
                    
                    {metric.is_scored ? (
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <span style={{ color: textColor, fontWeight: '500' }}>
                            Score: <strong style={{ color: textColor, fontSize: '1.1em' }}>{metric.score !== null ? metric.score.toFixed(1) : 'N/A'}</strong>
                          </span>
                          <span
                            style={{
                              padding: '0.25rem 0.75rem',
                              borderRadius: '4px',
                              fontSize: '0.75em',
                              fontWeight: 'bold',
                              background: metric.status === 'IN_RANGE' ? '#4caf50' :
                                         metric.status === 'BELOW' ? '#ff9800' :
                                         metric.status === 'ABOVE' ? '#ff9800' : '#999',
                              color: '#fff'
                            }}
                          >
                            {metric.status}
                          </span>
                        </div>
                        <div style={{ fontSize: '0.85em', color: '#555', fontWeight: '500' }}>
                          Target: {metric.target_range.min} - {metric.target_range.max} {metric.unit}
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: '#666', fontSize: '0.9em', fontStyle: 'italic' }}>
                        Not scored: {metric.notes.join('; ')}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Tier 2 Metrics */}
          {v2Response.tier2_metrics.length > 0 && (
            <div className="tier2-metrics">
              <details style={{ marginBottom: '1rem' }}>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold', marginBottom: '1rem', color: '#424242', fontSize: '1.1em', padding: '0.5rem', background: '#fafafa', borderRadius: '4px' }}>
                  Technical Insights (Tier 2 - Not Scored)
                </summary>
                <div className="metrics-grid">
                  {v2Response.tier2_metrics.map((metric) => (
                    <div
                      key={metric.key}
                      className="metric-card"
                      style={{
                        border: '2px solid #e0e0e0',
                        borderRadius: '8px',
                        padding: '1rem',
                        marginBottom: '1rem',
                        background: '#f5f5f5',
                        color: '#333'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '0.5rem' }}>
                        <div>
                          <strong style={{ color: '#333', fontSize: '1em' }}>{metric.name}</strong>
                          <span style={{ marginLeft: '0.5rem', fontSize: '0.85em', color: '#555', fontWeight: '500' }}>
                            {metric.value.toFixed(1)}{metric.unit}
                          </span>
                        </div>
                        <span
                          style={{
                            padding: '0.25rem 0.5rem',
                            borderRadius: '4px',
                            fontSize: '0.75em',
                            fontWeight: 'bold',
                            background: '#757575',
                            color: '#fff'
                          }}
                        >
                          {metric.confidence_level} (Insight Only)
                        </span>
                      </div>
                      {metric.target_range && (
                        <div style={{ fontSize: '0.85em', color: '#555', fontWeight: '500' }}>
                          Reference Range: {metric.target_range.min} - {metric.target_range.max} {metric.unit}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </details>
            </div>
          )}

          {/* V2 Notes */}
          {v2Response.notes && v2Response.notes.length > 0 && (
            <div className="v2-notes" style={{ marginTop: '1rem', padding: '1rem', background: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
              <strong style={{ color: '#856404' }}>Notes:</strong>
              <ul style={{ marginTop: '0.5rem', marginLeft: '1.5rem', color: '#856404' }}>
                {v2Response.notes.map((note, idx) => (
                  <li key={idx} style={{ marginBottom: '0.25rem' }}>{note}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Legacy Metrics Display (fallback) */}
      {!useV2 && scoringResult && (
        <>
          {/* Expandable Metric Details Table */}
          <MetricDetailsTable metricScores={scoringResult.metricScores} />
        </>
      )}

      {/* Single Camera Notice */}
      <SingleCameraNotice />

      {/* Action Buttons */}
      {(onSave || onDelete || onBack) && (
        <div className="panel-actions">
          {!isSaved && onSave && (
            <button
              className="action-button save-button"
              onClick={onSave}
            >
              💾 Save to Library
            </button>
          )}
          {onDelete && (
            <button
              className="action-button delete-button"
              onClick={onDelete}
            >
              🗑️ {isSaved ? 'Delete from Library' : 'Discard'}
            </button>
          )}
          {onBack && (
            <button
              className="action-button back-button"
              onClick={onBack}
            >
              ← Back
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default VideoKinematicsResultsPanel;

